import { useEffect, useMemo, useState } from 'react';
import { DefaultGenerics, StreamChat } from 'stream-chat';
import { useGetUserInfoQuery } from 'generated/graphql';
import { isEmpty } from 'lodash';
import toast from 'react-hot-toast';
import { chatChannelType, getChannelListOptions } from '../helpers';
import { InboxTab } from './useInbox.viewModel';

type UseBlockedUsersListProps = {
  userId: string;
  chatClient: StreamChat<DefaultGenerics> | null;
  activeTab: InboxTab;
};

export const useBlockedUsersList = ({
  userId,
  chatClient,
  activeTab,
}: UseBlockedUsersListProps) => {
  const [blockedChannels, setBlockedChannels] = useState<
    Array<{ userId: number; id: string }>
  >([]);
  const [fetchingChannels, setFetchingChannels] = useState(false);
  const { data, loading } = useGetUserInfoQuery({
    variables: {
      userIds: blockedChannels.map((ch) => ch.userId),
    },
    skip: isEmpty(blockedChannels),
  });

  const blockedUsersListOptions = useMemo(
    () => getChannelListOptions(userId, true),
    [userId],
  );

  const getBlockedUsers = async (chatClient: StreamChat) => {
    try {
      const channels = await chatClient.queryChannels(
        blockedUsersListOptions.filters,
      );
      const users: Array<{ userId: string; id: string }> = [];

      channels.forEach((channel) => {
        Object.values(channel.state.members).forEach((member) => {
          member.user &&
            users.push({ userId: member.user.id, id: channel.id || '' });
        });
      });
      const blockedUsersData = users
        .filter((ch) => !ch.userId.startsWith('creator'))
        .map((ch) => ({ ...ch, userId: Number(ch.userId) }));
      setBlockedChannels(blockedUsersData);
    } catch (error) {
      toast.error('Could not load blocked users');
    } finally {
      setFetchingChannels(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'blockedUsers' && chatClient) {
      setFetchingChannels(true);
      getBlockedUsers(chatClient);
    }
  }, [activeTab, chatClient]);

  const unblockUser = async (userId: number) => {
    if (chatClient) {
      const blockedChannel = blockedChannels.find((ch) => ch.userId === userId);
      if (!blockedChannel) {
        toast.error('Could not unblock user');
        return;
      }

      const channel = chatClient.channel(
        chatChannelType,
        blockedChannel?.id,
        {},
      );
      try {
        await channel.updatePartial({ set: { frozen: false } });
      } catch (error) {
        toast.error('Could not unblock user');
      }

      getBlockedUsers(chatClient);
    }
  };

  return {
    blockedUsers: data?.getUsersInfoByIds || [],
    loadingBlockedUsers: fetchingChannels || loading,
    unblockUser,
  };
};
